import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Form, InputGroup, Spinner } from "react-bootstrap";
import { FiSearch } from "react-icons/fi";
import {
    getApiScopeFromValue,
    getChildScope,
    getScopeFromValue,
} from "@features/invitations/utils/getScopeFromParam";
import { useChildEntities } from "@features/admin/invitations/hooks/useChildEntities";

export interface ActiveScope {
    scope: string;
    scopeRefId: number;
    name?: string;
}

interface InviteScopeSelectProps {
    ownScope: string;
    ownScopeRefId: number;
    ownLabel: string;
    childPluralLabel: string;
    onChange: (active: ActiveScope) => void;
    fullWidth?: boolean;
    activeOnly?: boolean;
}

const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// react-bootstrap's `Dropdown` trips its generic children typing under React 19
// (Toggle + Menu read as "multiple children"); alias to a loose type so it
// type-checks. Runtime is identical to `Dropdown`. (Same workaround as TableActions.)
const Dd = Dropdown as unknown as React.FC<React.PropsWithChildren<Record<string, any>>>;

/**
 * Scope control for the invitations toolbar:
 *   [ This Nation ][ Districts ▾ ]
 * Built as a plain flex pair (role="group"), NOT a nested Bootstrap
 * ButtonGroup — nesting .btn-group > .btn-group fights the flex-grow/w-100
 * utilities used for the `fullWidth` layout (negative margins and
 * border-radius resets meant for fixed-width split buttons), so we use the
 * two controls as ordinary flex children instead.
 */
const InviteScopeSelect: React.FC<InviteScopeSelectProps> = ({
    ownScope,
    ownScopeRefId,
    ownLabel,
    childPluralLabel,
    onChange,
    fullWidth = false,
    activeOnly = false,
}) => {
    const ownCanonical = useMemo(() => getScopeFromValue(ownScope), [ownScope]);
    const ownApiScope = useMemo(() => getApiScopeFromValue(ownScope), [ownScope]);
    const childScope = useMemo(() => getChildScope(ownScope), [ownScope]);

    const [selectedChildId, setSelectedChildId] = useState<number | null>(null);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const searchRef = useRef<HTMLInputElement>(null);

    const { options, loading, error } = useChildEntities(ownApiScope, ownScopeRefId, !!childScope, activeOnly);

    const viewingOwn = selectedChildId === null;
    const selectedChild = useMemo(
        () => options.find((o) => String(o.value) === String(selectedChildId)),
        [options, selectedChildId]
    );
    const rightLabel = selectedChild ? selectedChild.label : cap(childPluralLabel);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query]);

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        if (selectedChildId !== null && !options.some((o) => String(o.value) === String(selectedChildId))) {
            setSelectedChildId(null);
        }
    }, [selectedChildId, options]);

    useEffect(() => {
        if (viewingOwn || !childScope) {
            onChangeRef.current({ scope: ownCanonical, scopeRefId: ownScopeRefId });
        } else {
            onChangeRef.current({ scope: childScope, scopeRefId: Number(selectedChildId), name: selectedChild?.label });
        }
    }, [viewingOwn, selectedChildId, childScope, ownCanonical, ownScopeRefId, selectedChild]);

    if (!childScope) return null;

    const selectChild = (id: number) => {
        setSelectedChildId(id);
        setOpen(false);
        setQuery("");
    };

    const toggleLabelStyle = fullWidth
        ? undefined
        : { maxWidth: 160, overflow: "hidden" as const, textOverflow: "ellipsis" as const, whiteSpace: "nowrap" as const };

    return (
        <div
            className={`d-flex${fullWidth ? " w-100 ntcog-scope-split" : ""}`}
            role="group"
            aria-label="Choose which invitations to view"
            data-testid="invite-scope-select"
        >
            <Button
                size="sm"
                variant={viewingOwn ? "soft-primary" : "light"}
                className={`rounded-end-0${fullWidth ? " flex-shrink-0" : ""}`}
                onClick={() => {
                    setSelectedChildId(null);
                    setOpen(false);
                }}
                data-testid="scope-own-pill"
            >
                {ownLabel}
            </Button>

            <Dd
                className={fullWidth ? "flex-grow-1 min-w-0" : undefined}
                show={open}
                autoClose="outside"
                onToggle={(next: boolean) => {
                    setOpen(next);
                    if (next) setTimeout(() => searchRef.current?.focus(), 0);
                    else setQuery("");
                }}
            >
                <Dropdown.Toggle
                    size="sm"
                    variant={!viewingOwn ? "soft-primary" : "light"}
                    className={`d-flex align-items-center rounded-start-0${fullWidth ? " w-100" : ""}`}
                    title={rightLabel}
                    data-testid="scope-child-toggle"
                >
                    {/* fullWidth: label grows to fill the toggle and truncates with an
                        ellipsis only when out of room, so it can never collapse to blank
                        and never spills past the card edge. The caret (::after) follows it. */}
                    <span
                        className={fullWidth ? "flex-grow-1 min-w-0 text-truncate text-start" : undefined}
                        style={toggleLabelStyle}
                    >
                        {rightLabel}
                    </span>
                </Dropdown.Toggle>

                <Dropdown.Menu
                    className="p-2 shadow-sm"
                    style={{ minWidth: 280 }}
                    data-testid="scope-dropdown"
                    renderOnMount
                >
                    <InputGroup size="sm" className="mb-2">
                        <InputGroup.Text>
                            <FiSearch />
                        </InputGroup.Text>
                        <Form.Control
                            ref={searchRef as any}
                            placeholder={`Search ${childPluralLabel}…`}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            data-testid="scope-dropdown-search"
                        />
                    </InputGroup>

                    <div style={{ maxHeight: 280, overflowY: "auto" }} data-testid="scope-dropdown-list">
                        {loading && (
                            <div className="text-center text-muted small py-3">
                                <Spinner size="sm" animation="border" className="me-2" />
                                Loading…
                            </div>
                        )}
                        {!loading && error && (
                            <div className="text-danger small px-2 py-3">Couldn't load {childPluralLabel}.</div>
                        )}
                        {!loading && !error && filtered.length === 0 && (
                            <div className="text-muted small px-2 py-3">No {childPluralLabel} found.</div>
                        )}
                        {!loading && !error &&
                            filtered.map((o) => (
                                <Dropdown.Item
                                    key={o.value}
                                    as="button"
                                    type="button"
                                    active={String(o.value) === String(selectedChildId)}
                                    onClick={() => selectChild(Number(o.value))}
                                    data-testid={`scope-option-${o.value}`}
                                >
                                    {o.label}
                                </Dropdown.Item>
                            ))}
                    </div>
                </Dropdown.Menu>
            </Dd>
        </div>
    );
};

export default InviteScopeSelect;